import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface PersistedDraftPayload<TDraft> {
  // Version du schema persiste pour invalider proprement les anciens brouillons.
  version: number;
  updatedAt: string;
  isMinimized: boolean;
  draft: TDraft;
}

interface MinimizableDraftState<TDraft> {
  isActive: boolean;
  isMinimized: boolean;
  draft: TDraft;
}

interface UseMinimizablePersistedDraftOptions<TDraft> {
  storageNamespace: string;
  userScope: string | number | undefined;
  draftVersion?: number;
  expandOnRestore?: boolean;
  createInitialDraft: () => TDraft;
}

interface UseMinimizablePersistedDraftReturn<TDraft> {
  isHydrated: boolean;
  storageKey?: string;
  state: MinimizableDraftState<TDraft>;
  startDraft: (draftOverride?: TDraft) => void;
  patchDraft: (patch: Partial<TDraft>) => void;
  updateDraft: (updater: (previous: TDraft) => TDraft) => void;
  minimizeDraft: () => void;
  expandDraft: () => void;
  clearDraft: () => void;
  clearPersistedDraft: () => void;
}

function readFromLocalStorage<TDraft>(
  key: string,
): PersistedDraftPayload<TDraft> | null {
  try {
    const rawValue = window.localStorage.getItem(key);
    if (!rawValue) {
      return null;
    }
    return JSON.parse(rawValue) as PersistedDraftPayload<TDraft>;
  } catch (error) {
    return null;
  }
}

function writeToLocalStorage<TDraft>(
  key: string,
  value: PersistedDraftPayload<TDraft>,
) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // no-op: on garde le formulaire utilisable meme si le storage echoue.
  }
}

function removeFromLocalStorage(key: string) {
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    // no-op: suppression best-effort.
  }
}

function toInactiveState<TDraft>(createInitialDraft: () => TDraft) {
  return {
    isActive: false,
    isMinimized: false,
    draft: createInitialDraft(),
  } satisfies MinimizableDraftState<TDraft>;
}

function persistDraftState<TDraft>(
  storageKey: string,
  draftVersion: number,
  state: MinimizableDraftState<TDraft>,
) {
  // Etat inactif => pas de brouillon persiste.
  if (!state.isActive) {
    removeFromLocalStorage(storageKey);
    return;
  }

  writeToLocalStorage(storageKey, {
    version: draftVersion,
    updatedAt: new Date().toISOString(),
    isMinimized: state.isMinimized,
    draft: state.draft,
  });
}

export default function useMinimizablePersistedDraft<TDraft>({
  storageNamespace,
  userScope,
  draftVersion = 1,
  expandOnRestore = true,
  createInitialDraft,
}: UseMinimizablePersistedDraftOptions<TDraft>): UseMinimizablePersistedDraftReturn<TDraft> {
  // Le ref evite de capturer une version obsolete de createInitialDraft
  // dans les callbacks memoises.
  const createInitialDraftRef = useRef(createInitialDraft);
  createInitialDraftRef.current = createInitialDraft;

  const storageKey = useMemo(() => {
    if (!userScope) {
      return undefined;
    }
    return `${storageNamespace}:${userScope}`;
  }, [storageNamespace, userScope]);

  const [isHydrated, setIsHydrated] = useState(false);
  const [state, setState] = useState<MinimizableDraftState<TDraft>>(() =>
    toInactiveState(createInitialDraftRef.current),
  );

  const resetToInactive = useCallback(() => {
    setState(toInactiveState(createInitialDraftRef.current));
  }, []);

  useEffect(() => {
    // Hydratation: lecture du brouillon persiste pour l'utilisateur courant.
    setIsHydrated(false);

    if (!storageKey) {
      resetToInactive();
      setIsHydrated(true);
      return;
    }

    const persistedDraft = readFromLocalStorage<TDraft>(storageKey);
    const isValid = persistedDraft && persistedDraft.version === draftVersion;
    if (!isValid) {
      // Brouillon absent/invalide: on nettoie et on repart d'un etat vierge.
      removeFromLocalStorage(storageKey);
      resetToInactive();
      setIsHydrated(true);
      return;
    }

    const shouldExpand = expandOnRestore;
    const nextState: MinimizableDraftState<TDraft> = {
      isActive: true,
      isMinimized: shouldExpand ? false : persistedDraft.isMinimized,
      draft: persistedDraft.draft,
    };
    setState(nextState);

    if (shouldExpand && persistedDraft.isMinimized) {
      // Si on force l'ouverture, on synchronise aussi le flag persiste.
      writeToLocalStorage(storageKey, {
        ...persistedDraft,
        isMinimized: false,
        updatedAt: new Date().toISOString(),
      });
    }

    setIsHydrated(true);
  }, [draftVersion, expandOnRestore, resetToInactive, storageKey]);

  useEffect(() => {
    if (!isHydrated || !storageKey) {
      return;
    }

    // Toute mutation du state hydrate est refletee dans localStorage.
    persistDraftState(storageKey, draftVersion, state);
  }, [draftVersion, isHydrated, state, storageKey]);

  const startDraft = useCallback((draftOverride?: TDraft) => {
    setState({
      isActive: true,
      isMinimized: false,
      draft: draftOverride ?? createInitialDraftRef.current(),
    });
  }, []);

  const patchDraft = useCallback((patch: Partial<TDraft>) => {
    setState((previous) => {
      // On n'accepte pas de patch tant que le brouillon n'est pas actif.
      if (!previous.isActive) {
        return previous;
      }
      return {
        ...previous,
        draft: { ...previous.draft, ...patch },
      };
    });
  }, []);

  const updateDraft = useCallback((updater: (previous: TDraft) => TDraft) => {
    setState((previous) => {
      if (!previous.isActive) {
        return previous;
      }
      return {
        ...previous,
        draft: updater(previous.draft),
      };
    });
  }, []);

  const minimizeDraft = useCallback(() => {
    setState((previous) => {
      if (!previous.isActive) {
        return previous;
      }
      return { ...previous, isMinimized: true };
    });
  }, []);

  const expandDraft = useCallback(() => {
    setState((previous) => {
      if (!previous.isActive) {
        return previous;
      }
      return { ...previous, isMinimized: false };
    });
  }, []);

  const clearDraft = useCallback(() => {
    resetToInactive();
  }, [resetToInactive]);

  const clearPersistedDraft = useCallback(() => {
    if (!storageKey) {
      return;
    }
    removeFromLocalStorage(storageKey);
  }, [storageKey]);

  return {
    isHydrated,
    storageKey,
    state,
    startDraft,
    patchDraft,
    updateDraft,
    minimizeDraft,
    expandDraft,
    clearDraft,
    clearPersistedDraft,
  };
}
